import { Component, OnInit } from '@angular/core';
import {
	PasswordInput,
	PasswordValidatorType,
	UsernameInput,
} from '@mpt-fo/common/kamona/components/kamona-input-field/kamona-input-field.component';
import { AuthenticationService } from '@mpt-fo/common/core/services/authentication.service';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { ActivatedRoute, Router } from '@angular/router';
import { MobileWebViewService } from '@mpt-fo/common/core/services/mobile-web-view.service';
import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { CountdownType } from '@mpt-fo/common/kamona/components/otp-count-down/otp-count-down.component';
import { DelegateType } from '@mpt-fo/common/shared/models/utility.model';
import { MobileDelegateService } from '@mpt-fo/common/core/services/mobile-delegate.service';
import {
	DEFAULT_DIALOG_CLASSES,
	DEFAULT_FAILED_OTP_MODAL_WIDTH,
	DEFAULT_MODAL_KAMONA_CLASSES,
	DEFAULT_MODAL_WIDTH,
	MONIEPOINT_DESIGN_TYPE,
} from '@mpt-fo/common/shared/constants';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';
import { ErrorCode } from '@app/channels/onboarding/onboarding-v2/model/onboarding-v2-model';
import { IdentityProvider } from '@mpt-fo/common/kamona/model/kamonal-model';
import { AuthenticationEvents, DataLayerService } from '@mpt-fo/common/core/services/data-layer-service.service';
import { LocationHelper } from '@mpt-fo/common/core/helpers/location.helper';

@Component({
	selector: 'app-password-recovery',
	templateUrl: './password-recovery.component.html',
	styleUrls: ['./password-recovery.component.scss'],
})
export class PasswordRecoveryComponent implements OnInit {
	currentStep: any;
	readonly TOTAL_STEP = 2;
	readonly USERNAME = 1;
	readonly PHONE_VERIFICATION = 2;
	readonly INITIAL_STEP = 0;
	otpInput: PasswordInput;
	// tslint:disable-next-line:variable-name
	readonly pin_hint = 'Enter OTP sent to your phone';
	readonly TRANSACTION_PIN_LENGTH = 6;
	readonly CountdownType = CountdownType;
	formSteps = [];
	usernameInput: UsernameInput;
	disableUsernameButton: boolean;
	disableOtpButton: boolean;
	pin: string;
	submitting: boolean;
	ussdCode: any;
	suspensionReleaseTime: any;
	otpError: string;
	runningModal: NzModalRef;
	isSuspensionOver = true;
	maxOtpReached: boolean;
	showMessage: boolean;
	errorMessage: string;
	readonly PIN_LENGTH = 6;
	ROUTE = '/mobile/recovery/password';
	useOtp = false;
	livelinessCheckReference = '';
	identityProvider = '';
	readonly feature = 'PASSWORD_RESET';

	constructor(
		private authService: AuthenticationService,
		private modalService: ModalService,
		private route: ActivatedRoute,
		private router: Router,
		private mobileService: MobileWebViewService,
		private delegateService: MobileDelegateService,
		private screenManager: ScreenManagerService,
		private dataLayerService: DataLayerService,
		private locationHelper: LocationHelper,
	) {}

	ngOnInit(): void {
		this.formSteps = this.createSteps();
		if (!this.screenManager.isMobile()) {
			this.routeToWeb();
		}
		this.screenManager.isMobile$.subscribe((isMobile) => {
			if (!isMobile && this.router?.url === this.ROUTE) {
				this.routeToWeb();
			}
		});
		this.route.queryParams.subscribe((params) => {
			this.initialize(params?.username || null);
			params?.username ? this.regenerateOTP() : this.setFormStep(this.INITIAL_STEP);
		});
	}

	async getEventData(errorDetails?: { errorCode?: string; errorMessage: string; }) {
		const geoHeaders = await this.locationHelper.getCoordinates();

		return {
			auth_type: this.useOtp ? 'OTP' : 'FACE_MATCH_CHECK',
			auth_type_provider: this.useOtp ? '' : this.identityProvider,
			feature: this.feature,
			...(geoHeaders ? {
				longitude: geoHeaders.longitude,
				latitude: geoHeaders.latitude,
			} : {}),
			...(errorDetails ? {
				api_error_code: errorDetails.errorCode,
				api_error_message: errorDetails.errorMessage,
				error_message: errorDetails.errorMessage,
			} : {}),
		};
	}

	createSteps () {
		return [
			{
				index: 0,
				step: 1,
				title: 'Password Recovery',
				description: 'Lets get you sorted out',
				previous: 'Go to login',
			},
			{
				index: 1,
				step: 2,
				title: 'Verify Phone Number',
				description: 'We’ve just sent you a 6 digit code. ' + 'Check your messages and enter it here.',
				previous: 'Password Recovery',
			},
			{
				index: 2,
				step: 3,
				title: '',
				description: '',
				previous: '',
			},
		];
	}

	routeToWeb() {
		this.router.navigateByUrl('password-reset');
	}

	initialize(username) {
		this.usernameInput = new UsernameInput(username, 'Enter username', true);
		this.otpInput = new PasswordInput(null, 'Enter OTP', true, PasswordValidatorType.PIN);
		this.otpInput.passwordHint = this.pin_hint;
		this.otpInput.setMaxLength(this.TRANSACTION_PIN_LENGTH);
	}

	setUsername($event) {
		this.usernameInput = $event;
		this.disableUsernameButton = this.usernameInput.validate();
	}

	validateUsername() {
		this.submitting = true;
		this.resetOtpSuspensionParams();
		const param = {
			username: this.usernameInput.getInput(),
		};
		this.authService
			.generatePasswordOTP(param)
			.then((res) => {
				this.useOtp = true;
				if (this.mobileService.isMobileWebViewUser()) {
					this.listenForOtp();
				}
				this.setOtpForm(res);
				this.setFormStep(this.USERNAME);
				this.setShowMessage(false);
				this.submitting = false;
				this.dataLayerService.logEventWithData(AuthenticationEvents.initiated, this.getEventData());
			})
			.catch((err) => {
				this.submitting = false;
				this.useOtp = err?.error?.code !== ErrorCode.LIVELINESS_REQUIRED;
				if (!this.useOtp) {
					this.formSteps[1].title = 'Face Verification';
					this.formSteps[1].description = '';
					this.livelinessCheckReference = err?.error?.reference || '';
					this.identityProvider = err?.error?.provider || IdentityProvider.METAMAP;
					this.setFormStep(this.USERNAME);
					this.dataLayerService.logEventWithData(AuthenticationEvents.initiated, this.getEventData());
				} else {
					this.setFormStep(this.INITIAL_STEP);
					this.handleError(err, 'Try Again');
				}
			});
	}

	listenForOtp() {
		this.delegateService
			.getDelegateData(DelegateType.listenForOtp)
			.then((res) => {})
			.catch((error) => {
				console.log('Error calling listenForOtp delegate ', error);
			});
	}

	regenerateOTP() {
		this.validateUsername();
	}

	setFormStep(index = 0) {
		this.currentStep = this.formSteps[index];
	}

	setOtpForm(res) {
		this.ussdCode = res?.ussdCode;
		this.otpInput.passwordHint = res?.otpMessage;
	}

	handleError(err: any, errorModalButtonName: string) {
		const description = err?.error?.errorMessage || StringHelper.getErrorMessageHttpErrorResponse(err, err.error?.description);
		this.errorMessage = description;
		const isUserSuspended = err?.error?.code === '400209' || err?.error?.code === '400208';
		const maxOtpReached = err?.error?.code === '403005';
		isUserSuspended || maxOtpReached
			? this.setSuspensionOtpDetails(err, description, maxOtpReached)
			: this.lunchErrorModal(errorModalButtonName, description);
	}

	setSuspensionOtpDetails(err: any, description, maxOtpReached = false) {
		const suspensionReleaseTime = err?.error?.suspensionReleaseTime;
		this.launchFullAlertModal({
			description,
			expiryTime: new Date(`${suspensionReleaseTime}`).getTime(),
		});
	}

	launchAlertModal(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'error';
		config.nzComponentParams.title = 'Validation Error';
		config.className = DEFAULT_MODAL_KAMONA_CLASSES;
		config.nzWidth = DEFAULT_MODAL_WIDTH;
		config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
		config.nzComponentParams.callbackName = params?.callbackName || 'Close';
		config.nzComponentParams.message = params?.description;
		this.runningModal = this.modalService.alert(config);
	}

	launchFullAlertModal(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'error';
		config.nzComponentParams.title = 'You’ve been suspended';
		config.className = DEFAULT_DIALOG_CLASSES;
		config.nzWidth = DEFAULT_FAILED_OTP_MODAL_WIDTH;
		config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
		config.nzComponentParams.expiryTime = params?.expiryTime;
		config.nzComponentParams.callbackName = 'Retry OTP';
		config.nzComponentParams.message = params?.description;
		this.runningModal = this.modalService.newalert(config);
	}

	lunchErrorModal(buttonName, description) {
		this.launchAlertModal({
			title: 'Request Failed',
			description,
			callbackName: buttonName,
		});
	}

	setPin($event: string) {
		this.pin = $event;
		this.disableOtpButton = $event.length === this.PIN_LENGTH;
	}

	resetOtpSuspensionParams() {
		this.suspensionReleaseTime = null;
		this.otpError = null;
		this.maxOtpReached = false;
	}

	validatePin() {
		this.submitting = true;
		this.resetOtpSuspensionParams();
		const param = {
			username: this.usernameInput.getInput(),
			otp: this.pin,
		};
		this.authService
			.validatePasswordOTP(param)
			.then((res) => {
				this.dataLayerService.logEventWithData(AuthenticationEvents.success, this.getEventData());
				this.navigateToChangePassword(param.username, res?.validationKey);
			})
			.catch((err) => {
				const description = err?.error?.errorMessage || StringHelper.getErrorMessageHttpErrorResponse(err, err.error?.description);
				this.dataLayerService.logEventWithData(AuthenticationEvents.failed, this.getEventData({
					errorCode: err?.error?.code,
					errorMessage: description,
				}));
				this.handleError(err, 'Close');
				this.submitting = false;
			});
	}

	navigateToChangePassword(username, validationKey) {
		this.router.navigate([`/mobile/change-password`], {
			queryParams: {
				validationKey: validationKey,
				username: username,
			},
		})
		.then(() => {
			this.submitting = false;
		});
	}

	goBack(index = 0) {
		index ? this.setFormStep() : this.router.navigateByUrl(`/login`);
	}

	setSuspensionOver($event: any) {
		this.isSuspensionOver = $event;
	}

	setShowMessage(showMessage) {
		this.showMessage = showMessage;
	}

	livelinessStepCompleted($evt) {
		if ($evt === -1) {
			this.goBack(1);
			return;
		}
		this.navigateToChangePassword(this.usernameInput.getInput(), this.livelinessCheckReference);
	}
}
